import { useMemo, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Table,
  Tag,
  Space,
  Select,
  Empty,
  Spin,
  Avatar,
  Typography,
  Alert,
  Tooltip,
  Modal,
  Button,
  Divider,
} from 'antd';
import {
  CheckCircleTwoTone,
  WarningOutlined,
  UserOutlined,
  FileSearchOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useBatches } from '../hooks/useBatches';
import {
  adminPageWrap,
  adminAlertStyle,
  adminFilterCardStyle,
  adminContentCardStyle,
} from '../styles/adminPageShell';
import { ANCHOR_COLORS } from '../utils/scoreColor';
import { taskStatusLabel, taskStatusColor } from '../utils/taskStatusDisplay';

const { Text, Title } = Typography;

interface DispatchSubordinate {
  id: number;
  name: string;
  empNo: string;
  position: string | null;
  deptPath: string | null;
  status: string;
  weightedScore: number | null;
  redLineFlag: boolean;
  submitAt: string | null;
}

interface DispatchGroup {
  leader: {
    id: number;
    name: string;
    empNo: string;
    position: string | null;
    deptPath: string | null;
  };
  total: number;
  submitted: number;
  pending: number;
  percent: number;
  subordinates: DispatchSubordinate[];
}

interface CompetencyDimension {
  code: string;
  name: string;
  weight: number;
  anchors: Record<string, string>;
}

interface CompetencyRedLine {
  code: string;
  desc: string;
}

interface CompetencyModel {
  id: number;
  position: string;
  versionNo: number;
  effectiveAt: string;
  dimensions: CompetencyDimension[];
  redLines: CompetencyRedLine[];
  applicableEmpIds: number[];
  applicablePersons: { id: number; name: string }[];
}

const REDLINE_CN: Record<string, string> = { V1: '红线一', V2: '红线二', V3: '红线三' };

function shortDept(path: string | null | undefined) {
  if (!path) return '-';
  const parts = path.split(/[\/\\>›→]/).map((s) => s.trim()).filter(Boolean);
  if (parts.length <= 2) return parts.join(' / ');
  return parts.slice(-2).join(' / ');
}

/**
 * 客户端版 resolveModel —— 与后端 CompetencyService.resolveModel 同优先级：
 *   1) applicableEmpIds 显式命中
 *   2) 模型职位 === 员工职位（精确）
 *   3) 模型职位按 & / 、 ， , ； ; 拆分后包含员工职位（合并→单一）
 *   4) 员工职位拆分后包含模型职位（单一→合并 反向）
 * 同优先级取 versionNo 最大的。
 */
function resolveCompetencyModel(
  models: CompetencyModel[],
  position: string | null | undefined,
  evaluateeId: number,
): CompetencyModel | null {
  if (!models || models.length === 0) return null;
  const empPos = (position || '').trim();

  const explicit = models.find((m) => m.applicableEmpIds?.includes(evaluateeId));
  if (explicit) return explicit;

  if (!empPos) return null;

  const splitPos = (s: string) =>
    s
      .split(/[&/、，,；;]/)
      .map((x) => x.trim())
      .filter(Boolean);

  const exact = models.find((m) => (m.position || '').trim() === empPos);
  if (exact) return exact;

  const merged = models.find((m) => splitPos(m.position || '').includes(empPos));
  if (merged) return merged;

  const empParts = splitPos(empPos);
  if (empParts.length > 1) {
    const reverse = models.find((m) => empParts.includes((m.position || '').trim()));
    if (reverse) return reverse;
  }
  return null;
}

export function CompetencyDispatchPage() {
  const { batches, batchId, setBatchId, isLoading: loadingBatches } = useBatches();
  const [progressFilter, setProgressFilter] = useState<
    'all' | 'notStarted' | 'inProgress' | 'done'
  >('all');
  const [leaderIds, setLeaderIds] = useState<number[]>([]);
  const [redlineFilter, setRedlineFilter] = useState<'all' | 'has' | 'none'>('all');
  /** 「查看评分标准」Modal */
  const [rubricSub, setRubricSub] = useState<DispatchSubordinate | null>(null);
  /** 「查看详情」Modal - 展示某领导下所有被评价人 */
  const [activeLeaderId, setActiveLeaderId] = useState<number | null>(null);

  const { data = [], isLoading } = useQuery<DispatchGroup[]>({
    queryKey: ['admin', 'competency-dispatch', batchId],
    queryFn: () =>
      api.get('/admin/competency/dispatch', { params: { batchId } }) as Promise<DispatchGroup[]>,
    enabled: !!batchId,
  });

  /** 全量胜任力模型（含维度/锚定/红线/适用人员），点「评分标准」时按 position+empId 解析 */
  const { data: models = [] } = useQuery<CompetencyModel[]>({
    queryKey: ['admin', 'competency-models'],
    queryFn: () => api.get('/admin/competency/models') as Promise<CompetencyModel[]>,
  });

  const overall = useMemo(() => {
    const totalLeaders = data.length;
    const finishedLeaders = data.filter((g) => g.percent === 100).length;
    const totalEvaluatees = data.reduce((s, g) => s + g.total, 0);
    const submittedEvaluatees = data.reduce((s, g) => s + g.submitted, 0);
    const percent =
      totalEvaluatees === 0 ? 0 : Math.round((submittedEvaluatees * 100) / totalEvaluatees);
    return { totalLeaders, finishedLeaders, totalEvaluatees, submittedEvaluatees, percent };
  }, [data]);

  /** 直属领导（评价人）下拉选项：来自当前批次的派单领导列表 */
  const leaderOptions = useMemo(
    () =>
      data
        .map((g) => ({
          value: g.leader.id,
          label: g.leader.name,
          searchText: g.leader.name + (g.leader.empNo ?? ''),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'zh-CN')),
    [data],
  );

  const filtered = useMemo(() => {
    const leaderSet = leaderIds.length ? new Set(leaderIds) : null;
    return data.filter((g) => {
      if (progressFilter === 'done' && g.percent !== 100) return false;
      if (progressFilter === 'inProgress' && (g.percent === 0 || g.percent === 100)) return false;
      if (progressFilter === 'notStarted' && g.percent !== 0) return false;
      if (leaderSet && !leaderSet.has(g.leader.id)) return false;
      if (redlineFilter !== 'all') {
        const hasRed = g.subordinates.some((s) => s.redLineFlag);
        if (redlineFilter === 'has' && !hasRed) return false;
        if (redlineFilter === 'none' && hasRed) return false;
      }
      return true;
    });
  }, [data, progressFilter, leaderIds, redlineFilter]);

  const columns = [
    {
      title: '直属领导（评价人）',
      key: 'leader',
      width: 180,
      render: (_: unknown, g: DispatchGroup) => (
        <Space size={8}>
          <Avatar size={30} icon={<UserOutlined />} style={{ background: '#722ed1' }}>
            {g.leader.name?.[0]}
          </Avatar>
          <span style={{ fontWeight: 600 }}>{g.leader.name}</span>
        </Space>
      ),
    },
    {
      title: '岗位 / 部门',
      key: 'pos',
      width: 220,
      ellipsis: true,
      render: (_: unknown, g: DispatchGroup) => (
        <div style={{ lineHeight: 1.35 }}>
          <div
            style={{
              fontSize: 13,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={g.leader.position ?? ''}
          >
            {g.leader.position ?? '-'}
          </div>
          <div
            style={{
              fontSize: 12,
              color: '#8c8c8c',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={g.leader.deptPath ?? ''}
          >
            {shortDept(g.leader.deptPath)}
          </div>
        </div>
      ),
    },
    {
      title: '需评价',
      dataIndex: 'total',
      key: 'total',
      width: 90,
      align: 'right' as const,
      sorter: (a: DispatchGroup, b: DispatchGroup) => a.total - b.total,
      render: (v: number) => <b>{v}</b>,
    },
    {
      title: '完成进度',
      key: 'progress',
      width: 280,
      sorter: (a: DispatchGroup, b: DispatchGroup) => a.percent - b.percent,
      defaultSortOrder: 'ascend' as const,
      render: (_: unknown, g: DispatchGroup) => {
        const rateColor =
          g.total === 0
            ? 'default'
            : g.percent === 100
              ? 'success'
              : g.percent >= 80
                ? 'blue'
                : g.percent > 0
                  ? 'orange'
                  : 'red';
        return (
          <Space size={4} wrap>
            <Tag color="success">已提交 {g.submitted}</Tag>
            <Tag color={g.pending > 0 ? 'warning' : 'default'}>
              待提交 {g.pending}
            </Tag>
            <Tag color={rateColor} style={{ fontWeight: 600 }}>
              完成率 {g.total ? `${g.percent}%` : '-'}
            </Tag>
          </Space>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_: unknown, g: DispatchGroup) => (
        <Button
          type="link"
          size="small"
          onClick={() => setActiveLeaderId(g.leader.id)}
          disabled={g.subordinates.length === 0}
        >
          查看详情
        </Button>
      ),
    },
  ];

  const subColumns = [
    {
      title: '被评价人',
      key: 'name',
      width: 140,
      render: (_: unknown, s: DispatchSubordinate) => (
        <Space size={8}>
          <Avatar size={26} icon={<UserOutlined />}>
            {s.name?.[0]}
          </Avatar>
          <span style={{ fontWeight: 500 }}>{s.name}</span>
        </Space>
      ),
    },
    {
      title: '岗位',
      dataIndex: 'position',
      key: 'position',
      width: 140,
      render: (v: string | null) => v || '-',
    },
    {
      title: '部门',
      key: 'dept',
      width: 200,
      render: (_: unknown, s: DispatchSubordinate) => shortDept(s.deptPath),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (v: string) => <Tag color={taskStatusColor(v)}>{taskStatusLabel(v)}</Tag>,
    },
    {
      title: '加权分',
      dataIndex: 'weightedScore',
      key: 'weightedScore',
      width: 90,
      align: 'right' as const,
      render: (v: number | null) => (v == null ? '-' : v.toFixed(2)),
    },
    {
      title: '红线',
      dataIndex: 'redLineFlag',
      key: 'redLineFlag',
      width: 70,
      align: 'center' as const,
      render: (v: boolean) =>
        v ? (
          <Tooltip title="存在红线命中">
            <WarningOutlined style={{ color: '#ff4d4f' }} />
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      title: '提交时间',
      dataIndex: 'submitAt',
      key: 'submitAt',
      width: 160,
      render: (v: string | null) =>
        v ? new Date(v).toLocaleString('zh-CN', { hour12: false }) : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 110,
      render: (_: unknown, s: DispatchSubordinate) => (
        <Button
          type="link"
          size="small"
          icon={<FileSearchOutlined />}
          onClick={() => setRubricSub(s)}
        >
          评分标准
        </Button>
      ),
    },
  ];

  const rubricModel = useMemo(
    () => (rubricSub ? resolveCompetencyModel(models, rubricSub.position, rubricSub.id) : null),
    [models, rubricSub],
  );

  /** 当前查看详情的领导分组 */
  const activeLeader = useMemo(
    () => data.find((g) => g.leader.id === activeLeaderId) || null,
    [data, activeLeaderId],
  );

  return (
    <div style={adminPageWrap}>
      <div className="tg-page-title">岗位胜任力 · 派单</div>

      <Alert
        type="info"
        showIcon
        message="按直属领导维度查看派单进度"
        description="每位被评价人由其直属领导一对一打分。下表按「领导」分组：可看到每位评价人需评价多少人、已提交多少、完成率多少；点「查看详情」展开下属状态/加权分/红线，再点「评分标准」查看该下属对应的胜任力模型。"
        style={{ ...adminAlertStyle, marginTop: 8, marginBottom: 12 }}
      />

      {/* 筛选 + 顶部 4 项汇总（去掉独立大卡，统计内嵌入筛选卡，与素质项派单视觉一致） */}
      <Card style={{ ...adminFilterCardStyle, marginBottom: 12 }} styles={{ body: { paddingBottom: 12 } }}>
        <Space wrap size={12}>
          <span>批次：</span>
          <Select
            value={batchId}
            onChange={setBatchId}
            style={{ width: 220 }}
            loading={loadingBatches}
            options={batches.map((b) => ({ value: b.id, label: b.name }))}
            placeholder="选择批次"
          />
          <span style={{ marginLeft: 8 }}>完成情况：</span>
          <Select
            value={progressFilter}
            onChange={setProgressFilter}
            style={{ width: 130 }}
            options={[
              { value: 'all', label: '全部' },
              { value: 'notStarted', label: '未开始' },
              { value: 'inProgress', label: '进行中' },
              { value: 'done', label: '已完成' },
            ]}
          />
          <span style={{ marginLeft: 8 }}>直属领导：</span>
          <Select
            mode="multiple"
            allowClear
            value={leaderIds}
            onChange={setLeaderIds}
            options={leaderOptions}
            placeholder="可多选"
            maxTagCount={3}
            showSearch
            filterOption={(input, opt) =>
              String(opt?.searchText || opt?.label || '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            style={{ width: 300 }}
          />
          <span style={{ marginLeft: 8 }}>红线命中：</span>
          <Select
            value={redlineFilter}
            onChange={setRedlineFilter}
            style={{ width: 130 }}
            options={[
              { value: 'all', label: '全部' },
              { value: 'has', label: '有命中' },
              { value: 'none', label: '无命中' },
            ]}
          />
        </Space>

        <Row gutter={16} style={{ marginTop: 12 }}>
          <Col span={5}>
            <Statistic title="评价人（领导）" value={overall.totalLeaders} suffix="人" />
          </Col>
          <Col span={5}>
            <Statistic
              title="已派完领导"
              value={overall.finishedLeaders}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleTwoTone twoToneColor="#52c41a" />}
              suffix="人"
            />
          </Col>
          <Col span={5}>
            <Statistic title="被评价人" value={overall.totalEvaluatees} suffix="人" />
          </Col>
          <Col span={5}>
            <Statistic
              title="已提交评价"
              value={overall.submittedEvaluatees}
              suffix={overall.totalEvaluatees ? `/ ${overall.totalEvaluatees}` : ''}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="完成率"
              value={overall.percent}
              suffix="%"
              valueStyle={{ color: overall.percent === 100 ? '#52c41a' : '#1677ff' }}
            />
          </Col>
        </Row>
        <Progress
          percent={overall.percent}
          size="small"
          showInfo={false}
          strokeColor={{ '0%': '#722ed1', '100%': '#52c41a' }}
          style={{ marginTop: 8 }}
        />
      </Card>

      {/* 表格 */}
      <Card style={adminContentCardStyle} styles={{ body: { paddingTop: 12, paddingBottom: 12 } }}>
        {!batchId ? (
          <Empty description="请先选择批次" />
        ) : isLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : filtered.length === 0 ? (
          <Empty description="没有符合条件的派单" />
        ) : (
          <Table<DispatchGroup>
            rowKey={(g) => g.leader.id}
            columns={columns}
            dataSource={filtered}
            size="middle"
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `共 ${t} 位评价人` }}
          />
        )}
      </Card>

      {/* 评分标准 Modal */}
      <Modal
        open={!!rubricSub}
        onCancel={() => setRubricSub(null)}
        footer={null}
        width={860}
        destroyOnClose
        title={
          rubricSub ? (
            <Space>
              <span style={{ fontSize: 16, fontWeight: 600 }}>{rubricSub.name}</span>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {rubricSub.position || '-'} · {shortDept(rubricSub.deptPath)}
              </Text>
              {rubricModel ? (
                <Tag color="purple">v{rubricModel.versionNo}</Tag>
              ) : (
                <Tag color="warning">未匹配到模型</Tag>
              )}
            </Space>
          ) : (
            '评分标准'
          )
        }
      >
        {rubricSub && !rubricModel && (
          <Alert
            type="warning"
            showIcon
            message="未在模型库中匹配到该下属的胜任力模型"
            description={
              <span>
                请到「岗位胜任力评价 · 模型库」检查：模型职位名是否覆盖
                <Tag style={{ margin: '0 4px' }}>{rubricSub.position || '—'}</Tag>
                ，或在该岗位模型的「适用人员」里把
                <Tag style={{ margin: '0 4px' }}>{rubricSub.name}</Tag>
                勾上。
              </span>
            }
          />
        )}

        {rubricSub && rubricModel && (
          <div>
            <Card size="small" style={{ marginBottom: 12, background: '#f5f7ff' }}>
              <Space size="middle" wrap>
                <span>
                  匹配模型：<b>{rubricModel.position}</b>
                </span>
                <Tag color="blue">{rubricModel.dimensions.length} 维度</Tag>
                <Tag color="red">{rubricModel.redLines.length} 条红线</Tag>
                {rubricModel.applicablePersons?.length > 0 && (
                  <Tag color="purple">指定适用 {rubricModel.applicablePersons.length} 人</Tag>
                )}
              </Space>
            </Card>

            <Title level={5} style={{ margin: '4px 0 12px' }}>
              评价维度（每维 5 档锚定 · 1 分最差，5 分最优）
            </Title>
            {rubricModel.dimensions.map((d, i) => (
              <Card
                key={d.code || i}
                size="small"
                style={{ marginBottom: 10 }}
                title={
                  <Space>
                    <Tag color="blue">维度 {i + 1}</Tag>
                    <span>{d.name}</span>
                    <Tag color="purple">权重 {d.weight}%</Tag>
                  </Space>
                }
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <div
                    key={n}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      padding: '5px 0',
                      borderBottom: n === 5 ? 'none' : '1px dashed #f0f0f0',
                    }}
                  >
                    <Tag
                      color={ANCHOR_COLORS[String(n)]}
                      style={{ minWidth: 44, textAlign: 'center', margin: 0 }}
                    >
                      {n} 分
                    </Tag>
                    <span style={{ color: '#444', flex: 1, lineHeight: 1.7 }}>
                      {d.anchors?.[String(n)] || '—'}
                    </span>
                  </div>
                ))}
              </Card>
            ))}

            <Divider style={{ margin: '16px 0 12px' }} />
            <Title level={5} style={{ margin: 0 }}>
              <WarningOutlined style={{ color: '#ff4d4f', marginRight: 6 }} />
              红线（任一触发即取消晋升评优资格）
            </Title>
            {rubricModel.redLines.length === 0 ? (
              <Empty description="未配置红线" style={{ margin: '12px 0' }} />
            ) : (
              <div style={{ marginTop: 10 }}>
                {rubricModel.redLines.map((r) => (
                  <Card
                    key={r.code}
                    size="small"
                    style={{ marginBottom: 8, borderLeft: '4px solid #ff4d4f' }}
                    bodyStyle={{ padding: 12 }}
                  >
                    <Space align="start">
                      <Tag color="red">{REDLINE_CN[r.code] || r.code}</Tag>
                      <span style={{ color: '#444' }}>{r.desc}</span>
                    </Space>
                  </Card>
                ))}
              </div>
            )}

            <Divider style={{ margin: '16px 0 12px' }} />
            <Card size="small" bodyStyle={{ padding: 12 }} style={{ background: '#fafafa' }}>
              <Space size="middle" wrap>
                <span>
                  当前状态：
                  <Tag color={taskStatusColor(rubricSub.status)} style={{ marginLeft: 4 }}>
                    {taskStatusLabel(rubricSub.status)}
                  </Tag>
                </span>
                {rubricSub.weightedScore != null && (
                  <span>
                    加权分：<b>{rubricSub.weightedScore.toFixed(2)}</b>
                  </span>
                )}
                {rubricSub.redLineFlag && (
                  <Tag color="error" icon={<WarningOutlined />}>
                    存在红线命中
                  </Tag>
                )}
                {rubricSub.submitAt && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    提交时间 {new Date(rubricSub.submitAt).toLocaleString('zh-CN', { hour12: false })}
                  </Text>
                )}
              </Space>
            </Card>
          </div>
        )}
      </Modal>

      {/* 查看详情 Modal —— 展示某领导下所有被评价人 */}
      <Modal
        open={!!activeLeader}
        onCancel={() => setActiveLeaderId(null)}
        footer={null}
        width={1100}
        destroyOnClose
        title={
          activeLeader ? (
            <Space size={8} wrap>
              <Avatar size={28} icon={<UserOutlined />} style={{ background: '#722ed1' }}>
                {activeLeader.leader.name?.[0]}
              </Avatar>
              <span style={{ fontSize: 16, fontWeight: 600 }}>
                {activeLeader.leader.name}
              </span>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {activeLeader.leader.position || '-'} · {shortDept(activeLeader.leader.deptPath)}
              </Text>
              <Tag color="processing">需评价 {activeLeader.total}</Tag>
              <Tag color="success">已提交 {activeLeader.submitted}</Tag>
              <Tag color={activeLeader.pending > 0 ? 'warning' : 'default'}>
                待提交 {activeLeader.pending}
              </Tag>
              <Tag
                color={
                  activeLeader.percent === 100
                    ? 'success'
                    : activeLeader.percent >= 80
                      ? 'blue'
                      : activeLeader.percent > 0
                        ? 'orange'
                        : 'red'
                }
                style={{ fontWeight: 600 }}
              >
                完成率 {activeLeader.percent}%
              </Tag>
            </Space>
          ) : (
            '派单详情'
          )
        }
      >
        {activeLeader && (
          <Table<DispatchSubordinate>
            rowKey="id"
            size="small"
            columns={subColumns}
            dataSource={activeLeader.subordinates}
            pagination={false}
            rowClassName={(r) => (r.status === 'PENDING' ? 'tg-row-pending' : '')}
          />
        )}
      </Modal>
    </div>
  );
}
